import {Box, Typography, Avatar, Tooltip, Chip} from "@mui/material";
import moment from "moment/moment";
import {styled} from "@mui/system";
import {grey, amber} from "@mui/material/colors";

import ContentTypeIcon from "./ContentTypeIcon";
import {COMPACT_TIME_FORMAT} from "../../config/DateTimeConstants";
import {retrieveServiceTypeIcon} from "../../helpers/ServiceTypeUtils"
import {BREAK_TIMESLOT_TITLE, TRAINING_TIMESLOT_TITLE} from "../../views/MySessionsView";
import {PERFORMER_CHECK_IN_STATUS} from "../../domain/PerformerCheckInStatus";
import {buildPerformerCheckInDetails} from "../../helpers/ShiftHelper";
import React from "react";
import {SESSION_TYPE} from "../../domain/SessionType";

const CONTENT_TYPE_ICON_COLOR = amber[600];
const CONTENT_NOT_ASSESSED_COLOR = grey[900];

const ServiceTypeBox = styled(Box)(() => ({
    backgroundColor: "white",
    width: "1.3rem",
    padding: "0.1rem",
    borderRadius: "0.719rem",
    fontSize: "1rem"
}));

const TitleAvatar = styled(Avatar)(() => ({
    width: 18,
    height: 18,
    backgroundColor: 'white'
}));

const SessionLabelBox = styled(Box)(() => ({
    display: "flex",
    alignItems: "center",
    marginTop: "0.1rem"
}));

const PerformerCheckInStatusChip = styled(Chip)(({ backgroundColor }) => ({
    width: 24,
    height: 24,
    marginRight: 2,
    paddingLeft: 9,
    border: '0.03rem solid black',
    backgroundColor,
}));

export default function SessionLabel({event, shiftEvents}) {

    const isContentAssessed = event.isAssessed;
    const startDate = moment(event.start).format(COMPACT_TIME_FORMAT);
    const firstContentType = event.contentType?.split(',')[0];
    const isBreakTimeslot = event.sessionType === SESSION_TYPE.BREAK;
    const isTrainingTimeslot = event.sessionType === SESSION_TYPE.TRAINING;
    const shiftEvent = shiftEvents.find((shift) => shift.id === event.shiftId);
    
    const isCheckInSession = event.sessionType === SESSION_TYPE.CHECK_IN;
    const {
        icon: checkInStatusIcon,
        color: checkInStatusIconBackground,
        tooltip: checkInStatusEventTitle
    } = buildPerformerCheckInDetails(shiftEvent.performingFrom, shiftEvent.end, shiftEvent.performerCheckInTime, COMPACT_TIME_FORMAT);
    
    const hasPerformerNotCheckedInForShift = isCheckInSession && shiftEvent.checkInStatus !== PERFORMER_CHECK_IN_STATUS.SUCCESS;
    const checkInSuccessful = shiftEvent.checkInStatus === PERFORMER_CHECK_IN_STATUS.SUCCESS;
    
    const doesShiftContainNoActiveSessions = isBreakTimeslot || isTrainingTimeslot || hasPerformerNotCheckedInForShift || checkInSuccessful;
    
    const AVATAR_ICON_SIZE = 12;
    const AVATAR_ICON_COLOR = isContentAssessed ? "white" : CONTENT_NOT_ASSESSED_COLOR;
    const contentTypeIconStyle = {
        backgroundColor: isContentAssessed ? CONTENT_TYPE_ICON_COLOR : "white",
        marginLeft: "0.2rem",
        width: 20,
        height: 20
    };

    const labelStyle = (isNonTeachingTimeslot) => ({
        fontSize: 'inherit',
        fontWeight: isNonTeachingTimeslot ? 'bold' : 'normal',
        marginLeft: (firstContentType || hasPerformerNotCheckedInForShift || checkInSuccessful) ? '0.5rem' : '3.3rem',
        variant: "body2",
        textOverflow: 'ellipsis',
        overflow: 'hidden'
    });

    const buildEventTitle = () => {

        let eventTitle = event.title;

        if (isBreakTimeslot) {
            eventTitle = BREAK_TIMESLOT_TITLE;
        } else if (isTrainingTimeslot) {
            eventTitle = TRAINING_TIMESLOT_TITLE;
        } else if (isCheckInSession) {
            eventTitle = checkInStatusEventTitle;
        }

        return eventTitle;
    }
    
    const eventTitle = buildEventTitle();

    return <SessionLabelBox className="text-ellipsis">
        
        {
          (!hasPerformerNotCheckedInForShift && !checkInSuccessful && !isBreakTimeslot && !isTrainingTimeslot)
          &&
          (
            <ServiceTypeBox>
                <Tooltip title={event.serviceTypeDescription}>
                    <TitleAvatar>
                        <Box component="img" src={retrieveServiceTypeIcon([event.serviceTypeKeyCode])} sx={{width: 14}}/>
                    </TitleAvatar>
                </Tooltip>
            </ServiceTypeBox>
          )
        }

        {
          isCheckInSession
          &&
          <PerformerCheckInStatusChip icon={checkInStatusIcon} size='small' backgroundColor={checkInStatusIconBackground}/>
        }

        {
          firstContentType
          &&
          <Avatar sx={contentTypeIconStyle}>
              <ContentTypeIcon contentType={firstContentType} iconSize={AVATAR_ICON_SIZE} iconColor={AVATAR_ICON_COLOR}/>
          </Avatar>
        }
        
        <Tooltip title={doesShiftContainNoActiveSessions ? null : `${startDate} - ${eventTitle}`}>
            <Typography sx={labelStyle(doesShiftContainNoActiveSessions)}>{!(doesShiftContainNoActiveSessions) && `${startDate} - `} {eventTitle}</Typography>
        </Tooltip>
    </SessionLabelBox>;
}